import {
  SimpleGrid,
  Stack,
  Title,
  Text,
  Card,
  Loader,
  Center,
  Group,
  Badge,
  ColorSwatch,
  Table,
} from '@mantine/core';
import { BarChart, DonutChart } from '@mantine/charts';
import { useQuery } from '@tanstack/react-query';
import {
  IconPackage,
  IconBuildingFactory2,
  IconWeight,
  IconRuler2,
  IconTruck,
  IconPhoto,
} from '@tabler/icons-react';
import { api } from '../lib/api';
import { compact, num, pct } from '../lib/format';
import { MetricTile } from '../components/MetricTile';

interface Overview {
  total_parts: number;
  distinct_manufacturers: number;
  with_weight: number;
  with_full_dims: number;
  client_inventory_rows: number;
  distinct_stocked_parts: number;
  distinct_clients_stocked: number;
  raw_client_part_rows: number;
  enrichment_log: { status: string; count: number }[];
  specs_by_source: { source: string; count: number }[];
}

interface Bar {
  key: string;
  value: number;
  sub?: number;
}

export default function Dashboard() {
  // Long staleTime — backend already caches; extend client side so nav/back doesn't refetch.
  const metricsDefaults = { staleTime: 5 * 60_000, gcTime: 30 * 60_000 };

  const overview = useQuery({
    queryKey: ['metrics', 'overview'],
    queryFn: () => api<Overview>('/api/metrics/overview'),
    ...metricsDefaults,
  });
  const manufacturers = useQuery({
    queryKey: ['metrics', 'manufacturers'],
    queryFn: () =>
      api<{ manufacturer: string; total: number; with_weight: number }[]>(
        '/api/metrics/manufacturers',
      ),
    ...metricsDefaults,
  });
  const categories = useQuery({
    queryKey: ['metrics', 'categories'],
    queryFn: () => api<{ category: string; count: number }[]>('/api/metrics/categories'),
    ...metricsDefaults,
  });
  const funnel = useQuery({
    queryKey: ['metrics', 'stocking-funnel'],
    queryFn: () => api<Bar[]>('/api/metrics/stocking-funnel'),
    ...metricsDefaults,
  });
  const stockedCoverage = useQuery({
    queryKey: ['metrics', 'stocked-coverage'],
    queryFn: () =>
      api<{
        stocked_total: number;
        with_both: number;
        weight_only: number;
        dims_only: number;
        neither: number;
        not_in_master: number;
      }>('/api/metrics/stocked-coverage'),
    ...metricsDefaults,
  });
  const coverageHeatmap = useQuery({
    queryKey: ['metrics', 'coverage-heatmap'],
    queryFn: () =>
      api<
        {
          manufacturer: string;
          total: number;
          cummins: number;
          paccar: number;
          dealer: number;
          other: number;
          none: number;
        }[]
      >('/api/metrics/coverage-heatmap'),
    ...metricsDefaults,
  });
  const stockedGaps = useQuery({
    queryKey: ['metrics', 'stocked-gaps'],
    queryFn: () =>
      api<
        {
          manufacturer: string;
          stocked_count: number;
          missing_specs: number;
          with_specs: number;
        }[]
      >('/api/metrics/stocked-gaps'),
    ...metricsDefaults,
  });
  const imageStats = useQuery({
    queryKey: ['images', 'stats'],
    queryFn: () =>
      api<{
        summary: {
          unique_images: number;
          classified: number;
          useful: number;
          watermarked: number;
          parts_with_useful_image: number;
          parts_with_branded_only: number;
        };
      }>('/api/images/stats'),
    ...metricsDefaults,
  });

  const ov = overview.data;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Parts Hub</Title>
          <Text c="dimmed" size="sm">
            Overview of the parts-hub master data
          </Text>
        </Stack>
        {ov && (
          <Badge size="lg" variant="light" color="gray">
            {compact(ov.raw_client_part_rows)} raw obs · {compact(ov.client_inventory_rows)} stocked
          </Badge>
        )}
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <MetricTile
          label="Enriched parts"
          value={num(ov?.total_parts)}
          loading={overview.isLoading}
          icon={<IconPackage size={18} />}
          color="indigo"
          to="/parts"
        />
        <MetricTile
          label="Manufacturers"
          value={num(ov?.distinct_manufacturers)}
          loading={overview.isLoading}
          icon={<IconBuildingFactory2 size={18} />}
          color="grape"
        />
        <MetricTile
          label="Parts with weight"
          value={num(ov?.with_weight)}
          sub={ov && pct(ov.with_weight, ov.total_parts)}
          loading={overview.isLoading}
          icon={<IconWeight size={18} />}
          color="teal"
        />
        <MetricTile
          label="Parts with full dims"
          value={num(ov?.with_full_dims)}
          sub={ov && pct(ov.with_full_dims, ov.total_parts)}
          loading={overview.isLoading}
          icon={<IconRuler2 size={18} />}
          color="cyan"
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <MetricTile
          label="Stocked across fleet"
          value={num(ov?.distinct_stocked_parts)}
          sub={ov && `${ov.distinct_clients_stocked} client inventories`}
          loading={overview.isLoading}
          icon={<IconTruck size={18} />}
          color="orange"
        />
        <MetricTile
          label="Specs from Cummins API"
          value={num(ov?.specs_by_source.find((s) => s.source === 'cummins')?.count ?? 0)}
          loading={overview.isLoading}
        />
        <MetricTile
          label="Specs from PACCAR files"
          value={num(ov?.specs_by_source.find((s) => s.source === 'paccar')?.count ?? 0)}
          loading={overview.isLoading}
        />
        <MetricTile
          label="Specs from dealer harvest"
          value={num(ov?.specs_by_source.find((s) => s.source === 'dealer')?.count ?? 0)}
          loading={overview.isLoading}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
        <MetricTile
          label="Parts with image"
          value={num(imageStats.data?.summary.parts_with_useful_image)}
          sub={
            ov && imageStats.data
              ? pct(imageStats.data.summary.parts_with_useful_image, ov.total_parts)
              : undefined
          }
          loading={imageStats.isLoading || overview.isLoading}
          icon={<IconPhoto size={18} />}
          color="pink"
          to="/images"
        />
        <MetricTile
          label="Unique images stored"
          value={num(imageStats.data?.summary.unique_images)}
          sub={imageStats.data && `${num(imageStats.data.summary.classified)} classified`}
          loading={imageStats.isLoading}
        />
        <MetricTile
          label="Useful image rate"
          value={
            imageStats.data && imageStats.data.summary.classified > 0
              ? pct(imageStats.data.summary.useful, imageStats.data.summary.classified)
              : '—'
          }
          sub={imageStats.data && `${num(imageStats.data.summary.useful)} useful`}
          loading={imageStats.isLoading}
        />
        <MetricTile
          label="Watermarked images"
          value={num(imageStats.data?.summary.watermarked)}
          sub={
            imageStats.data && imageStats.data.summary.classified > 0
              ? pct(imageStats.data.summary.watermarked, imageStats.data.summary.classified)
              : undefined
          }
          loading={imageStats.isLoading}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Top manufacturers — coverage</Title>
            <Text c="dimmed" size="xs">
              Parts count with spec completion overlay (top 15)
            </Text>
            {manufacturers.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              <BarChart
                h={520}
                data={(manufacturers.data ?? []).slice(0, 15).map((r) => ({
                  manufacturer: r.manufacturer,
                  with_weight: r.with_weight,
                  without_weight: Math.max(0, r.total - r.with_weight),
                }))}
                dataKey="manufacturer"
                type="stacked"
                orientation="vertical"
                yAxisProps={{ width: 110 }}
                series={[
                  { name: 'with_weight', color: 'teal.6', label: 'with weight' },
                  { name: 'without_weight', color: 'indigo.4', label: 'without weight' },
                ]}
                valueFormatter={(v) => compact(v)}
                withLegend
                withBarValueLabel
              />
            )}
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Category distribution</Title>
            <Text c="dimmed" size="xs">
              Top 10 canonical categories
            </Text>
            {categories.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              (() => {
                const palette = [
                  'indigo.6',
                  'teal.6',
                  'orange.6',
                  'grape.6',
                  'cyan.6',
                  'pink.6',
                  'lime.6',
                  'yellow.7',
                  'red.6',
                  'blue.6',
                ];
                const slices = (categories.data ?? []).slice(0, 10).map((r, i) => ({
                  name: r.category,
                  value: r.count,
                  color: palette[i % palette.length]!,
                }));
                return (
                  <Group align="center" gap="lg" wrap="nowrap">
                    <DonutChart
                      h={340}
                      size={240}
                      thickness={38}
                      data={slices}
                      withTooltip
                      tooltipDataSource="segment"
                      chartLabel="Categories"
                    />
                    <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                      {slices.map((d) => {
                        const [name, shade] = d.color.split('.');
                        const cssColor = `var(--mantine-color-${name}-${shade})`;
                        return (
                          <Group gap={8} wrap="nowrap" key={d.name}>
                            <ColorSwatch color={cssColor} size={12} withShadow={false} />
                            <Text size="xs" style={{ flex: 1, minWidth: 0 }} truncate>
                              {d.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {num(d.value)}
                            </Text>
                          </Group>
                        );
                      })}
                    </Stack>
                  </Group>
                );
              })()
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Fleet stocking funnel</Title>
            <Text c="dimmed" size="xs">
              How many clients stock each unique part (PIV1)
            </Text>
            {funnel.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              <BarChart
                h={300}
                data={(funnel.data ?? []).map((r) => ({ bucket: r.key, parts: r.value }))}
                dataKey="bucket"
                series={[{ name: 'parts', color: 'orange.6' }]}
                withLegend={false}
              />
            )}
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Stocked parts — shipping spec coverage</Title>
            <Text c="dimmed" size="xs">
              Distinct parts stocked across client inventories, by spec completion
              {stockedCoverage.data && ` (${num(stockedCoverage.data.stocked_total)} total)`}
            </Text>
            {stockedCoverage.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              <BarChart
                h={300}
                data={[
                  {
                    label: 'Stocked',
                    'weight + dims': stockedCoverage.data?.with_both ?? 0,
                    'weight only': stockedCoverage.data?.weight_only ?? 0,
                    'dims only': stockedCoverage.data?.dims_only ?? 0,
                    'no specs': stockedCoverage.data?.neither ?? 0,
                    'not in master': stockedCoverage.data?.not_in_master ?? 0,
                  },
                ]}
                dataKey="label"
                type="stacked"
                orientation="vertical"
                yAxisProps={{ width: 80 }}
                series={[
                  { name: 'weight + dims', color: 'teal.6' },
                  { name: 'weight only', color: 'cyan.6' },
                  { name: 'dims only', color: 'yellow.7' },
                  { name: 'no specs', color: 'red.5' },
                  { name: 'not in master', color: 'gray.5' },
                ]}
                withLegend
              />
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Stocked coverage heatmap — manufacturer × source</Title>
            <Text c="dimmed" size="xs">
              Stocked parts only. For each top manufacturer, share of parts whose specs came from
              each source. Cell shading is row-relative percentage.
            </Text>
            {coverageHeatmap.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              (() => {
                const rows = coverageHeatmap.data ?? [];
                const cellBg = (
                  n: number,
                  total: number,
                  kind: 'good' | 'gap',
                ): string | undefined => {
                  if (!total || !n) {
                    return undefined;
                  }
                  const a = Math.min(0.55, (n / total) * 0.7);
                  return kind === 'good'
                    ? `rgba(20, 184, 166, ${a.toFixed(2)})`
                    : `rgba(239, 68, 68, ${a.toFixed(2)})`;
                };
                const cell = (n: number, total: number, kind: 'good' | 'gap') => (
                  <Table.Td
                    ta="right"
                    style={{
                      backgroundColor: cellBg(n, total, kind),
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {n ? (
                      <>
                        {num(n)}
                        <Text span size="xs" c="dimmed" ml={4}>
                          {total ? `${Math.round((n / total) * 100)}%` : ''}
                        </Text>
                      </>
                    ) : (
                      <Text span c="dimmed">
                        —
                      </Text>
                    )}
                  </Table.Td>
                );
                return (
                  <Table.ScrollContainer minWidth={560}>
                    <Table withRowBorders={false} verticalSpacing="xs" striped="even">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Manufacturer</Table.Th>
                          <Table.Th ta="right">Total</Table.Th>
                          <Table.Th ta="right">Cummins</Table.Th>
                          <Table.Th ta="right">PACCAR</Table.Th>
                          <Table.Th ta="right">Dealer</Table.Th>
                          <Table.Th ta="right">Other</Table.Th>
                          <Table.Th ta="right">None</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {rows.map((r) => (
                          <Table.Tr key={r.manufacturer}>
                            <Table.Td>{r.manufacturer}</Table.Td>
                            <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {num(r.total)}
                            </Table.Td>
                            {cell(r.cummins, r.total, 'good')}
                            {cell(r.paccar, r.total, 'good')}
                            {cell(r.dealer, r.total, 'good')}
                            {cell(r.other, r.total, 'good')}
                            {cell(r.none, r.total, 'gap')}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                );
              })()
            )}
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Stocked-but-missing-specs by manufacturer</Title>
            <Text c="dimmed" size="xs">
              Top 15 manufacturers ranked by stocked parts that lack at least one shipping spec
            </Text>
            {stockedGaps.isLoading ? (
              <Center p="xl">
                <Loader />
              </Center>
            ) : (
              <BarChart
                h={400}
                data={(stockedGaps.data ?? []).map((r) => ({
                  manufacturer: r.manufacturer,
                  missing: r.missing_specs,
                  with_specs: r.with_specs,
                }))}
                dataKey="manufacturer"
                type="stacked"
                orientation="vertical"
                yAxisProps={{ width: 110 }}
                series={[
                  { name: 'missing', color: 'red.5', label: 'missing specs' },
                  { name: 'with_specs', color: 'teal.6', label: 'with specs' },
                ]}
                withLegend
                withBarValueLabel
              />
            )}
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
